/**
 * Where a spin wheel comes to rest.
 *
 * The landing angle for the wheels that share `useSpinSession` — the daily
 * modal, the surface wheel and the gamification renderer. `MinelabRttrWheel`,
 * `FuelUpSpinWheelModal` and `ProgrammaticSpinWheel` still carry their own; RTTR
 * is live on a different drawing convention and is not being moved underneath a
 * running campaign.
 *
 * This existed five times, three different ways, and two of the copies were
 * wrong in the same manner: they added the server's `rotationDegrees` to the
 * wheel's previous angle. That value is an ABSOLUTE target measured from zero
 * (`fullTurns * 360 - (index * sweep + sweep / 2)`, see the backend's
 * `getSpinRotationDegrees`), so adding it only lands correctly while the wheel
 * happens to be resting at a multiple of 360 — true on the first spin of a
 * mount and never again. The second spin drifts by exactly the first spin's
 * offset and the wheel stops on a different prize than the one awarded, which
 * is what a member sees: "I landed on 50 but got 100."
 *
 * The fix is a DELTA. Take the previous angle, work out how far forward the
 * wheel must turn to bring the winning slice under the pointer, and add whole
 * turns for the animation. Whole turns only — a fractional turn carries an
 * arbitrary offset past the target, which is the same bug wearing a different
 * hat.
 *
 * GEOMETRY THIS ASSUMES, and every caller must draw to match: slice `i` spans
 * `i * sweep` to `(i + 1) * sweep`, measured CLOCKWISE from 12 o'clock, where
 * the pointer sits. Landing puts the slice's CENTRE under the pointer rather
 * than its leading edge — stopping on a boundary reads as ambiguous between two
 * prizes. This matches the backend's calculation exactly; the two must agree or
 * the wheel lies about what it paid.
 */

/** Whole turns of flourish before the wheel settles. */
export const SPIN_FULL_TURNS = 5;

/**
 * The next absolute rotation, in degrees, for a wheel resting at `previous`.
 *
 * Always turns FORWARD (clockwise) so the animation never snaps backwards.
 * Returns `previous` unchanged when there is nothing to land on — no slices, or
 * an index the wheel does not contain — so a bad outcome leaves the wheel where
 * it is rather than spinning to a meaningless angle. A ONE-slice wheel is a
 * valid wheel and does spin: the single slice's centre comes under the pointer.
 */
export function nextSpinRotation(
    previous: number,
    outcomeIndex: number,
    segmentCount: number,
    fullTurns: number = SPIN_FULL_TURNS,
): number {
    if (!Number.isFinite(previous) || segmentCount <= 0) return previous;
    if (!Number.isInteger(outcomeIndex) || outcomeIndex < 0 || outcomeIndex >= segmentCount) {
        return previous;
    }

    const sweep = 360 / segmentCount;
    /*
     * Negative: the wheel turns BACK by the slice's centre angle to bring it
     * under a pointer fixed at 12 o'clock.
     */
    const target = -(outcomeIndex * sweep + sweep / 2);
    /* Normalised to 0..360 so the forward distance is never negative. */
    const current = (previous % 360 + 360) % 360;
    const forward = ((target - current) % 360 + 360) % 360;
    return previous + fullTurns * 360 + forward;
}
